import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, CardBody, Checkbox, Icon, MainLayout, Modal, Select, TextArea, TextInput } from '../components';
import {
  CreatePublicTreeLinkRequest,
  familyTreesAPI,
  LivingPersonPublicLinkPreflightDto,
  LivingPersonPublishPreflightDto,
  publicTreeAdminAPI,
  PublicCommentAdminDto,
  PublicLinkAnalyticsDto,
  PublicTreeLinkAdminDto,
  PublicTreeLinkTokenResultDto,
} from '../services/api';
import { useAppNavigation } from '../contexts/AppNavigationContext';
import { brandClasses } from '../theme';

type TreeAccess = {
  id: string;
  name: string;
  role: string;
  canManageMembers?: boolean;
};

type LinkFormState = {
  id?: string;
  mode: 'create' | 'edit' | 'duplicate';
  name: string;
  slug: string;
  allowComments: boolean;
  allowEmbedding: boolean;
  showLastName: boolean;
  showPhoto: boolean;
  isActive: boolean;
};

const analyticsRangeOptions = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

const actionIconButtonClassName = `inline-flex h-9 w-9 items-center justify-center rounded-lg border ${brandClasses.subtleBorder} ${brandClasses.warmSurface} ${brandClasses.bodyText} ${brandClasses.hoverSurface}`;
const cardClassName = `${brandClasses.elevatedCard}`;

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return 'N/A';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

const formatDateOnly = (value?: string | null) => {
  if (!value) {
    return 'N/A';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string } } }).response;
    if (response?.data?.error) {
      return response.data.error;
    }
  }

  return fallback;
};

const normalizeSlugInput = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const createDefaultLinkForm = (): LinkFormState => ({
  mode: 'create',
  name: '',
  slug: '',
  allowComments: true,
  allowEmbedding: true,
  showLastName: true,
  showPhoto: true,
  isActive: true,
});

const TOKENS_STORAGE_KEY = (treeId: string) => `ft-public-link-tokens-${treeId}`;

const loadStoredTokens = (treeId: string): Record<string, PublicTreeLinkTokenResultDto> => {
  try {
    const stored = sessionStorage.getItem(TOKENS_STORAGE_KEY(treeId));
    return stored ? (JSON.parse(stored) as Record<string, PublicTreeLinkTokenResultDto>) : {};
  } catch {
    return {};
  }
};

const saveStoredTokens = (treeId: string, tokens: Record<string, PublicTreeLinkTokenResultDto>) => {
  try {
    sessionStorage.setItem(TOKENS_STORAGE_KEY(treeId), JSON.stringify(tokens));
  } catch {
    // ignore storage errors
  }
};

const copyTextToClipboard = async (value: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  // Fallback for non-secure contexts
  const tempTextArea = document.createElement('textarea');
  tempTextArea.value = value;
  tempTextArea.style.position = 'fixed';
  tempTextArea.style.opacity = '0';
  document.body.appendChild(tempTextArea);
  tempTextArea.focus();
  tempTextArea.select();
  document.execCommand('copy');
  document.body.removeChild(tempTextArea);
};

export const PublicTreeAdminPage: React.FC = () => {
  const { treeId } = useParams<{ treeId: string }>();
  const navigate = useNavigate();
  const { setNavigationState, clearNavigationState } = useAppNavigation();

  const publishSectionRef = useRef<HTMLDivElement | null>(null);
  const linksSectionRef = useRef<HTMLDivElement | null>(null);
  const moderationSectionRef = useRef<HTMLDivElement | null>(null);

  const [treeAccess, setTreeAccess] = useState<TreeAccess | null>(null);
  const [links, setLinks] = useState<PublicTreeLinkAdminDto[]>([]);
  const [versions, setVersions] = useState<Array<{ id: string; versionNumber: number; publishNote?: string | null; createdAt: string; createdByUserId: string }>>([]);
  const [comments, setComments] = useState<PublicCommentAdminDto[]>([]);
  const [selectedLinkId, setSelectedLinkId] = useState<string>('');
  const [analyticsRange, setAnalyticsRange] = useState('7d');
  const [analytics, setAnalytics] = useState<PublicLinkAnalyticsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishNote, setPublishNote] = useState('');
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [iframeModalOpen, setIframeModalOpen] = useState(false);
  const [activeIframeLinkId, setActiveIframeLinkId] = useState<string>('');
  const [linkForm, setLinkForm] = useState<LinkFormState>(createDefaultLinkForm());
  const [ephemeralLinkTokens, setEphemeralLinkTokens] = useState<Record<string, PublicTreeLinkTokenResultDto>>(
    () => (treeId ? loadStoredTokens(treeId) : {})
  );
  const [showLatestToken, setShowLatestToken] = useState<PublicTreeLinkTokenResultDto | null>(null);
  const [revealingLinkId, setRevealingLinkId] = useState<string>('');

  const latestVersion = versions[0] ?? null;
  const role = treeAccess?.role;
  const canPublishAndManageLinks = role === 'Owner' || role === 'Manager';
  const canModerate = role === 'Owner' || role === 'Manager' || role === 'Editor';
  const canViewAnalytics = role === 'Owner' || role === 'Manager';
  const hasPublishedVersion = versions.length > 0;

  const selectedLink = useMemo(
    () => links.find((link) => link.id === selectedLinkId) ?? null,
    [links, selectedLinkId]
  );

  const iframeLink = useMemo(
    () => links.find((link) => link.id === activeIframeLinkId) ?? null,
    [activeIframeLinkId, links]
  );

  const getSessionUrlData = useCallback((linkId: string) => {
    const tokenResult = ephemeralLinkTokens[linkId];
    if (!tokenResult) {
      return null;
    }

    const publicUrl = `${window.location.origin}${tokenResult.urlPath}`;
    const iframeCode = `<iframe src="${publicUrl}" width="100%" height="700" style="border:0;" allowfullscreen></iframe>`;
    return {
      publicUrl,
      iframeCode,
    };
  }, [ephemeralLinkTokens]);

  const loadPage = useCallback(async () => {
    if (!treeId) {
      setError('Tree not found.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const accessResponse = await familyTreesAPI.getTreeAccess(treeId);
      const access = accessResponse.data.data ?? accessResponse.data;
      setTreeAccess(access);

      const nextCanManagePublicTree = access?.role === 'Owner' || access?.role === 'Manager' || access?.role === 'Editor';
      setNavigationState({
        activeTreeId: treeId,
        canManageMembers: Boolean(access?.canManageMembers),
        canManagePublicTree: nextCanManagePublicTree,
      });

      if (!nextCanManagePublicTree) {
        setLinks([]);
        setVersions([]);
        setComments([]);
        return;
      }

      const isOwnerOrManager = access?.role === 'Owner' || access?.role === 'Manager';
      const canSeeModeration = access?.role === 'Owner' || access?.role === 'Manager' || access?.role === 'Editor';

      const [versionsResponse, linksResponse, commentsResponse] = await Promise.all([
        isOwnerOrManager ? familyTreesAPI.getPublishedVersions(treeId) : Promise.resolve(null),
        isOwnerOrManager ? publicTreeAdminAPI.getLinks(treeId) : Promise.resolve(null),
        canSeeModeration ? publicTreeAdminAPI.getModerationQueue(treeId) : Promise.resolve(null),
      ]);

      const nextVersions = versionsResponse?.data ?? [];
      const nextLinks = linksResponse?.data ?? [];
      const nextComments = commentsResponse?.data ?? [];

      setVersions(nextVersions);
      setLinks(nextLinks);
      setComments(nextComments);

      setSelectedLinkId((current) => {
        if (current && nextLinks.some((link) => link.id === current)) {
          return current;
        }

        return nextLinks[0]?.id ?? '';
      });
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'Failed to load public tree management data.'));
    } finally {
      setLoading(false);
    }
  }, [setNavigationState, treeId]);

  useEffect(() => {
    void loadPage();
    return () => {
      clearNavigationState();
    };
  }, [clearNavigationState, loadPage]);

  useEffect(() => {
    if (treeId) {
      saveStoredTokens(treeId, ephemeralLinkTokens);
    }
  }, [treeId, ephemeralLinkTokens]);

  useEffect(() => {
    const loadAnalytics = async () => {
      if (!selectedLinkId || !canViewAnalytics) {
        setAnalytics(null);
        return;
      }

      try {
        const response = await publicTreeAdminAPI.getAnalytics(selectedLinkId, analyticsRange);
        setAnalytics(response.data);
      } catch {
        setAnalytics(null);
      }
    };

    void loadAnalytics();
  }, [analyticsRange, canViewAnalytics, selectedLinkId]);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOpenCreateLinkModal = () => {
    setLinkForm(createDefaultLinkForm());
    setLinkModalOpen(true);
  };

  const handleOpenEditLinkModal = (link: PublicTreeLinkAdminDto) => {
    setLinkForm({
      id: link.id,
      mode: 'edit',
      name: link.name,
      slug: link.slug ?? '',
      allowComments: link.allowComments,
      allowEmbedding: link.allowEmbedding,
      showLastName: link.showLastName,
      showPhoto: link.showPhoto,
      isActive: link.isActive,
    });
    setLinkModalOpen(true);
  };

  const handleOpenDuplicateLinkModal = (link: PublicTreeLinkAdminDto) => {
    setLinkForm({
      id: undefined,
      mode: 'duplicate',
      name: `${link.name} Copy`,
      slug: '',
      allowComments: link.allowComments,
      allowEmbedding: link.allowEmbedding,
      showLastName: link.showLastName,
      showPhoto: link.showPhoto,
      isActive: true,
    });
    setLinkModalOpen(true);
  };

  const buildConsentRequest = (
    source: string,
    warningMessage: string,
    affectedLivingPeople: Array<{ personId: string; displayName: string }>
  ) => ({
    accepted: true,
    consentMessageVersion: 'living-public-display-consent-v1',
    warningTextSnapshot: warningMessage,
    consentSource: source,
    affectedPersonIds: affectedLivingPeople.map((person) => person.personId),
    affectedPersonNamesSnapshot: affectedLivingPeople.map((person) => person.displayName),
  });

  const requestConsentForPreflight = (
    source: string,
    preflight: LivingPersonPublishPreflightDto | LivingPersonPublicLinkPreflightDto
  ) => {
    if (!preflight.requiresLivingPersonPublicDisplayConsent) {
      return undefined;
    }

    const warningMessage = preflight.warningMessage
      || `This action will publicly display ${preflight.affectedLivingPeople.length} living people. Continue?`;

    const accepted = window.confirm(
      [preflight.warningTitle, warningMessage].filter(Boolean).join('\n\n')
    );

    if (!accepted) {
      return null;
    }

    return buildConsentRequest(source, warningMessage, preflight.affectedLivingPeople);
  };

  const regenerateLinkTokenWithConsent = async (linkId: string, source: string) => {
    if (!treeId) {
      return null;
    }

    const preflightResponse = await familyTreesAPI.getPublicLinkPreflight(treeId);
    const consent = requestConsentForPreflight(source, preflightResponse.data);
    if (consent === null) {
      return null;
    }

    const response = await publicTreeAdminAPI.regenerateLinkToken(linkId, {
      livingPersonPublicDisplayConsent: consent,
    });

    setEphemeralLinkTokens((current) => ({ ...current, [linkId]: response.data }));
    setLinks((current) => current.map((link) => (link.id === linkId ? response.data.link : link)));
    return response.data;
  };

  const handlePublish = async () => {
    if (!treeId) {
      return;
    }

    try {
      setPublishing(true);
      const preflightResponse = await familyTreesAPI.getPublishPreflight(treeId);
      const consent = requestConsentForPreflight('PublicTreePublish', preflightResponse.data);
      if (consent === null) {
        setPublishing(false);
        return;
      }

      await familyTreesAPI.publishTree(treeId, {
        publishNote: publishNote.trim() || undefined,
        livingPersonPublicDisplayConsent: consent,
      });
      setPublishModalOpen(false);
      setPublishNote('');
      setInfoMessage('Public tree published successfully.');
      await loadPage();
    } catch (publishError) {
      setError(getErrorMessage(publishError, 'Could not publish the tree. Please try again.'));
    } finally {
      setPublishing(false);
    }
  };

  const handleSaveLink = async () => {
    if (!treeId) {
      return;
    }

    if (!linkForm.name.trim()) {
      setError('Link name is required.');
      return;
    }

    try {
      setSaving(true);
      const payload: CreatePublicTreeLinkRequest = {
        name: linkForm.name.trim(),
        slug: linkForm.mode === 'edit' ? undefined : (normalizeSlugInput(linkForm.slug) || null),
        isActive: linkForm.isActive,
        allowComments: linkForm.allowComments,
        allowEmbedding: linkForm.allowEmbedding,
        showLastName: linkForm.showLastName,
        showPhoto: linkForm.showPhoto,
      };

      if (linkForm.mode === 'edit' && linkForm.id) {
        await publicTreeAdminAPI.updateLink(linkForm.id, payload);
        setInfoMessage('Public link updated successfully.');
      } else {
        const preflightResponse = await familyTreesAPI.getPublicLinkPreflight(treeId);
        const consent = requestConsentForPreflight('PublicTreeCreateLink', preflightResponse.data);
        if (consent === null) {
          setSaving(false);
          return;
        }

        payload.livingPersonPublicDisplayConsent = consent;
        const response = await publicTreeAdminAPI.createLink(treeId, payload);
        setEphemeralLinkTokens((current) => ({
          ...current,
          [response.data.link.id]: response.data,
        }));
        setShowLatestToken(response.data);
        setInfoMessage(linkForm.mode === 'duplicate' ? 'Public link duplicated successfully.' : 'Public link created successfully.');
      }

      setLinkModalOpen(false);
      await loadPage();
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'Could not save public link.'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLinkField = async (link: PublicTreeLinkAdminDto, field: 'isActive' | 'allowComments' | 'allowEmbedding', nextValue: boolean) => {
    try {
      await publicTreeAdminAPI.updateLink(link.id, { [field]: nextValue });
      const successMessages: Record<typeof field, string> = {
        isActive: nextValue ? 'Public link enabled.' : 'Public link disabled.',
        allowComments: nextValue ? 'Comments enabled for this public link.' : 'Comments disabled for this public link.',
        allowEmbedding: nextValue ? 'Embedding enabled for this public link.' : 'Embedding disabled for this public link.',
      };
      setInfoMessage(successMessages[field]);
      await loadPage();
    } catch (updateError) {
      setError(getErrorMessage(updateError, 'Failed to update public link.'));
    }
  };

  const handleRevealUrl = async (link: PublicTreeLinkAdminDto) => {
    try {
      setRevealingLinkId(link.id);
      await regenerateLinkTokenWithConsent(link.id, 'PublicTreeRevealLink');
    } catch {
      setError('Could not retrieve the URL. Please try again.');
    } finally {
      setRevealingLinkId('');
    }
  };

  const handleRegenerateToken = async (link: PublicTreeLinkAdminDto) => {
    try {
      const response = await regenerateLinkTokenWithConsent(link.id, 'PublicTreeRegenerateLink');
      if (!response) {
        return;
      }
      setEphemeralLinkTokens((current) => ({
        ...current,
        [link.id]: response,
      }));
      setShowLatestToken(response);
      // Update just this link in state — no full page reload needed
      setLinks((current) => current.map((l) => (l.id === link.id ? response.link : l)));
      setInfoMessage('Public link regenerated. The new URL is now shown in the table.');
    } catch (tokenError) {
      setError(getErrorMessage(tokenError, 'Failed to regenerate token.'));
    }
  };

  const handleDeleteLink = async (link: PublicTreeLinkAdminDto) => {
    const confirmed = window.confirm('Delete public link? This link will stop working immediately. Existing embeds using this link will no longer show the tree.');
    if (!confirmed) {
      return;
    }

    try {
      await publicTreeAdminAPI.deleteLink(link.id);
      setInfoMessage('Public link deleted.');
      await loadPage();
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, 'Failed to delete public link.'));
    }
  };

  const handleCopyPublicUrl = async (link: PublicTreeLinkAdminDto) => {
    let sessionUrlData = getSessionUrlData(link.id);
    if (!sessionUrlData) {
      try {
        const response = await regenerateLinkTokenWithConsent(link.id, 'PublicTreeCopyUrl');
        if (!response) {
          return;
        }
        sessionUrlData = {
          publicUrl: `${window.location.origin}${response.urlPath}`,
          iframeCode: `<iframe src="${window.location.origin}${response.urlPath}" width="100%" height="700" style="border:0;" allowfullscreen></iframe>`,
        };
      } catch {
        setError('Could not generate the public URL. Please try again.');
        return;
      }
    }

    try {
      await copyTextToClipboard(sessionUrlData.publicUrl);
      setInfoMessage('Public URL copied.');
    } catch {
      setError('Could not copy URL. Please copy it manually.');
    }
  };

  const handleCopyIframe = async (linkId: string) => {
    let sessionUrlData = getSessionUrlData(linkId);
    if (!sessionUrlData) {
      try {
        const response = await regenerateLinkTokenWithConsent(linkId, 'PublicTreeCopyIframe');
        if (!response) {
          return;
        }
        sessionUrlData = {
          publicUrl: `${window.location.origin}${response.urlPath}`,
          iframeCode: `<iframe src="${window.location.origin}${response.urlPath}" width="100%" height="700" style="border:0;" allowfullscreen></iframe>`,
        };
      } catch {
        setError('Could not generate iframe code. Please try again.');
        return;
      }
    }

    try {
      await copyTextToClipboard(sessionUrlData.iframeCode);
      setInfoMessage('Iframe code copied.');
    } catch {
      setError('Could not copy iframe code. Please copy it manually.');
    }
  };

  const handleOpenPublicPage = async (link: PublicTreeLinkAdminDto) => {
    let sessionUrlData = getSessionUrlData(link.id);
    if (!sessionUrlData) {
      try {
        const response = await regenerateLinkTokenWithConsent(link.id, 'PublicTreeOpenPage');
        if (!response) {
          return;
        }
        sessionUrlData = {
          publicUrl: `${window.location.origin}${response.urlPath}`,
          iframeCode: '',
        };
      } catch {
        setError('Could not generate the public URL. Please try again.');
        return;
      }
    }

    window.open(sessionUrlData.publicUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenIframeModal = async (link: PublicTreeLinkAdminDto) => {
    if (!link.allowEmbedding) {
      setError('Embedding is disabled for this public link.');
      return;
    }

    let sessionUrlData = getSessionUrlData(link.id);
    if (!sessionUrlData) {
      try {
        const response = await regenerateLinkTokenWithConsent(link.id, 'PublicTreeOpenIframe');
        if (!response) {
          return;
        }
      } catch {
        setError('Could not generate embed code. Please try again.');
        return;
      }
    }

    setActiveIframeLinkId(link.id);
    setIframeModalOpen(true);
  };

  const moderateComment = async (commentId: string, status: PublicCommentAdminDto['status']) => {
    try {
      await publicTreeAdminAPI.moderateComment(commentId, status);
      setInfoMessage(status === 'Approved' ? 'Comment approved.' : 'Comment rejected.');
      await loadPage();
    } catch (moderateError) {
      setError(getErrorMessage(moderateError, 'Failed to moderate comment.'));
    }
  };

  const deleteComment = async (commentId: string) => {
    const confirmed = window.confirm('Delete this comment? This removes the comment from the public tree.');
    if (!confirmed) {
      return;
    }

    try {
      await publicTreeAdminAPI.deleteComment(commentId);
      setInfoMessage('Comment deleted.');
      await loadPage();
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, 'Failed to delete comment.'));
    }
  };

  const iframeSnippet = iframeLink ? getSessionUrlData(iframeLink.id)?.iframeCode ?? '' : '';
  const publicUrlForIframeLink = iframeLink ? getSessionUrlData(iframeLink.id)?.publicUrl ?? '' : '';

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className={`text-4xl font-bold ${brandClasses.primaryText}`}>
              Public Tree: <span className={brandClasses.titleText}>{treeAccess?.name ?? 'Current Tree'}</span>
            </h1>
            <p className={`mt-3 text-base ${brandClasses.bodyText}`}>
              Publish and share a public, view-only version of this family tree.
            </p>
            <p className={`mt-4 text-sm ${brandClasses.subtleText}`}>
              {latestVersion
                ? `Latest published version: Version ${latestVersion.versionNumber} · ${formatDateTime(latestVersion.createdAt)}`
                : 'This tree has not been published yet.'}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="outline" onClick={() => navigate(treeId ? `/canvas/${treeId}` : '/trees')}>
              <Icon name="chevronLeft" size="sm" />
              Back to Current Tree
            </Button>
            <Button type="button" variant="outline" onClick={() => setHelpModalOpen(true)}>
              <Icon name="alertCircle" size="sm" />
              Help
            </Button>
          </div>
        </div>

        {error && <Alert variant="error" message={error} dismissible onDismiss={() => setError('')} />}
        {infoMessage && <Alert variant="info" message={infoMessage} dismissible onDismiss={() => setInfoMessage('')} />}

        {loading ? (
          <div className="grid gap-6 lg:grid-cols-2">
            {[1, 2, 3, 4].map((card) => (
              <Card key={card} className={cardClassName}>
                <CardBody className="space-y-4">
                  <div className="h-6 w-40 animate-pulse rounded bg-[#efe9db]" />
                  <div className="h-4 w-full animate-pulse rounded bg-[#f4efe4]" />
                  <div className="h-4 w-3/4 animate-pulse rounded bg-[#f4efe4]" />
                </CardBody>
              </Card>
            ))}
          </div>
        ) : !treeAccess || (!canModerate && !canPublishAndManageLinks) ? (
          <Card className={cardClassName}>
            <CardBody className="space-y-3">
              <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>Access restricted</h2>
              <p className={`text-sm ${brandClasses.bodyText}`}>
                Public tree management is available only to Owners, Managers, and Editors.
              </p>
            </CardBody>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className="grid gap-4 xl:grid-cols-[1fr_1fr_1fr_320px]">
              <button type="button" onClick={() => scrollToSection(publishSectionRef)} className={`rounded-2xl border px-6 py-5 text-left ${brandClasses.heroBorder} ${brandClasses.warmSurface} transition hover:shadow-md`}>
                <div className="flex items-start gap-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#446b23] text-white">1</div>
                  <div>
                    <p className={`text-xl font-semibold ${brandClasses.titleText}`}>Publish</p>
                    <p className={`mt-2 text-sm ${brandClasses.bodyText}`}>Create a public snapshot from your current private tree.</p>
                  </div>
                </div>
              </button>
              <button type="button" onClick={() => scrollToSection(linksSectionRef)} className={`rounded-2xl border px-6 py-5 text-left ${brandClasses.heroBorder} ${brandClasses.warmSurface} transition hover:shadow-md`}>
                <div className="flex items-start gap-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#446b23] text-white">2</div>
                  <div>
                    <p className={`text-xl font-semibold ${brandClasses.titleText}`}>Create Public Link</p>
                    <p className={`mt-2 text-sm ${brandClasses.bodyText}`}>Create one or more public links with your preferred settings.</p>
                  </div>
                </div>
              </button>
              <button type="button" onClick={() => scrollToSection(linksSectionRef)} className={`rounded-2xl border px-6 py-5 text-left ${brandClasses.heroBorder} ${brandClasses.warmSurface} transition hover:shadow-md`}>
                <div className="flex items-start gap-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#446b23] text-white">3</div>
                  <div>
                    <p className={`text-xl font-semibold ${brandClasses.titleText}`}>Share &amp; Embed</p>
                    <p className={`mt-2 text-sm ${brandClasses.bodyText}`}>Share the link or embed it on any website.</p>
                  </div>
                </div>
              </button>
              <div className={`rounded-2xl border px-6 py-5 ${brandClasses.heroBorder} bg-[#f5f8ee]`}>
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-[#5c7f38] text-white">
                    <Icon name="check" size="sm" />
                  </div>
                  <div>
                    <p className={`text-xl font-semibold ${brandClasses.titleText}`}>Privacy First</p>
                    <p className={`mt-2 text-sm ${brandClasses.bodyText}`}>Living people are hidden by default.</p>
                    <p className={`text-sm ${brandClasses.bodyText}`}>You control what is visible.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div ref={publishSectionRef} className="space-y-6">
                <Card className={cardClassName}>
                  <CardBody className="space-y-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className={`text-2xl font-semibold ${brandClasses.primaryText}`}>Publish Status</h2>
                        <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>
                          {latestVersion ? 'Your public tree is currently published from the latest snapshot.' : 'This tree has not been published publicly yet.'}
                        </p>
                      </div>
                      {canPublishAndManageLinks ? (
                        <Button type="button" variant="primary" onClick={() => setPublishModalOpen(true)}>
                          <Icon name="upload" size="sm" />
                          {latestVersion ? 'Publish New Version' : 'Publish Tree'}
                        </Button>
                      ) : null}
                    </div>

                    <div className={`rounded-2xl border ${brandClasses.subtleBorder}`}>
                      <div className="grid gap-0 md:grid-cols-[220px_minmax(0,1fr)]">
                        <div className="rounded-l-2xl bg-[#f2f6ea] px-5 py-6">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#4d7a28] text-white">
                              <Icon name={latestVersion ? 'check' : 'alertCircle'} size="sm" />
                            </div>
                            <div>
                              <p className={`text-xl font-semibold ${brandClasses.titleText}`}>{latestVersion ? 'Published' : 'Not Published'}</p>
                            </div>
                          </div>
                        </div>
                        <div className="px-5 py-6">
                          {latestVersion ? (
                            <div className="space-y-2">
                              <p className={`text-lg font-semibold ${brandClasses.primaryText}`}>Version {latestVersion.versionNumber}</p>
                              <p className={`text-sm ${brandClasses.bodyText}`}>Published on {formatDateTime(latestVersion.createdAt)}</p>
                              <p className={`text-sm ${brandClasses.bodyText}`}>by {treeAccess?.name ? 'Current owner/manager workflow user' : 'Current user'}</p>
                            </div>
                          ) : (
                            <p className={`text-sm ${brandClasses.bodyText}`}>This tree has not been published publicly yet.</p>
                          )}
                        </div>
                      </div>
                      <div className={`border-t px-5 py-4 ${brandClasses.subtleBorder}`}>
                        <p className={`text-sm font-semibold ${brandClasses.primaryText}`}>Last publish note</p>
                        <p className={`mt-2 text-sm ${brandClasses.bodyText}`}>{latestVersion?.publishNote || 'No publish note.'}</p>
                      </div>
                    </div>

                    <div className={`rounded-xl border px-4 py-4 ${brandClasses.subtleBorder} ${brandClasses.mutedSurface}`}>
                      <p className={`text-sm ${brandClasses.bodyText}`}>
                        Your public tree shows only the latest published version. Create a new version to make changes visible publicly.
                      </p>
                    </div>
                  </CardBody>
                </Card>
              </div>

              <Card className={cardClassName}>
                <CardBody className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className={`text-2xl font-semibold ${brandClasses.primaryText}`}>Version History</h2>
                      <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>Newest versions appear first.</p>
                    </div>
                    <button type="button" onClick={() => setInfoMessage('Full version history page is not added yet.')} className={`text-sm font-semibold text-[#b64f42]`}>
                      View all versions
                    </button>
                  </div>

                  {versions.length === 0 ? (
                    <p className={`text-sm ${brandClasses.bodyText}`}>No published versions yet.</p>
                  ) : (
                    <div className={`overflow-hidden rounded-2xl border ${brandClasses.subtleBorder}`}>
                      <div className={`grid grid-cols-[120px_1.2fr_1fr_1.2fr] gap-4 border-b px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] ${brandClasses.subtleBorder} ${brandClasses.subtleText}`}>
                        <span>Version</span>
                        <span>Published On</span>
                        <span>Published By</span>
                        <span>Note</span>
                      </div>
                      {versions.map((version, index) => (
                        <div key={version.id} className={`grid grid-cols-[120px_1.2fr_1fr_1.2fr] gap-4 px-4 py-4 ${index > 0 ? `border-t ${brandClasses.subtleBorder}` : ''}`}>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold ${brandClasses.primaryText}`}>Version {version.versionNumber}</span>
                            {index === 0 ? <Badge variant="success">Latest</Badge> : null}
                          </div>
                          <span className={`text-sm ${brandClasses.bodyText}`}>{formatDateTime(version.createdAt)}</span>
                          <span className={`text-sm ${brandClasses.bodyText}`}>Murali Inguva</span>
                          <span className={`truncate text-sm ${brandClasses.bodyText}`}>{version.publishNote || 'No publish note'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>

            <div ref={linksSectionRef} className="space-y-6">
              <Card className={cardClassName}>
                <CardBody className="space-y-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h2 className={`text-2xl font-semibold ${brandClasses.primaryText}`}>Public Links</h2>
                      <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>Create and manage public links for your published tree.</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button type="button" variant="primary" onClick={handleOpenCreateLinkModal} disabled={!canPublishAndManageLinks || !hasPublishedVersion}>
                        Create New Link
                        <Icon name="plus" size="sm" />
                      </Button>
                      <button type="button" onClick={() => setHelpModalOpen(true)} className={`text-sm font-semibold ${brandClasses.titleText}`}>
                        How links work
                      </button>
                    </div>
                  </div>

                  {!hasPublishedVersion && canPublishAndManageLinks ? (
                    <div className={`rounded-xl border px-4 py-4 ${brandClasses.subtleBorder} ${brandClasses.mutedSurface}`}>
                      <p className={`text-sm ${brandClasses.bodyText}`}>Publish the tree before creating a public link.</p>
                    </div>
                  ) : null}

                  {showLatestToken ? (
                    <div className={`rounded-xl border px-4 py-4 ${brandClasses.subtleBorder} bg-[#f5f8ee]`}>
                      <p className={`text-sm font-semibold ${brandClasses.primaryText}`}>Latest generated public URL</p>
                      <p className={`mt-2 break-all text-sm ${brandClasses.bodyText}`}>{`${window.location.origin}${showLatestToken.urlPath}`}</p>
                      <p className={`mt-2 text-xs ${brandClasses.subtleText}`}>
                        This share URL is shown now because the raw token is only available immediately after create or regenerate.
                      </p>
                    </div>
                  ) : null}

                  {links.length === 0 ? (
                    <p className={`text-sm ${brandClasses.bodyText}`}>No public links yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full border-separate border-spacing-y-3">
                        <thead>
                          <tr className={`text-left text-xs font-semibold uppercase tracking-[0.14em] ${brandClasses.subtleText}`}>
                            <th className="px-2">Name</th>
                            <th className="px-2">URL</th>
                            <th className="w-[100px] px-2 whitespace-nowrap">Status</th>
                            <th className="w-[100px] px-2 whitespace-nowrap">Comments</th>
                            <th className="px-2">Last Accessed</th>
                            <th className="w-[60px] px-2 whitespace-nowrap">Views</th>
                            <th className="w-[156px] px-2">Actions</th>
                            <th className="px-2">Embed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {links.map((link, index) => {
                            const sessionUrlData = getSessionUrlData(link.id);

                            return (
                              <tr key={link.id} className={`${brandClasses.warmSurface} shadow-[0_10px_20px_rgba(95,83,58,0.05)]`}>
                                <td className={`rounded-l-2xl border-y border-l px-3 py-4 align-top ${brandClasses.subtleBorder}`}>
                                  <p className={`font-semibold ${brandClasses.primaryText}`}>{link.name}</p>
                                  <div className="mt-1 flex flex-wrap items-center gap-2">
                                    {index === 0 ? <Badge variant="default">Default</Badge> : null}
                                    {link.pendingCommentsCount > 0 ? <Badge variant="warning">{link.pendingCommentsCount} pending</Badge> : null}
                                  </div>
                                  <p className={`mt-2 text-xs ${brandClasses.subtleText}`}>Created on {formatDateOnly(link.createdAt)}</p>
                                </td>
                                <td className={`border-y px-3 py-4 align-top ${brandClasses.subtleBorder}`}>
                                  {sessionUrlData ? (
                                    <p className={`max-w-[260px] truncate text-sm ${brandClasses.bodyText}`}>{sessionUrlData.publicUrl}</p>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => void handleRevealUrl(link)}
                                      disabled={revealingLinkId === link.id}
                                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium ${brandClasses.subtleBorder} ${brandClasses.warmSurface} ${brandClasses.titleText} ${brandClasses.hoverSurface} disabled:opacity-50`}
                                    >
                                      <Icon name="eye" size="sm" />
                                      {revealingLinkId === link.id ? 'Loading…' : 'Show URL'}
                                    </button>
                                  )}
                                  {link.slug ? <p className={`mt-1 text-xs ${brandClasses.subtleText}`}>Slug: {link.slug}</p> : null}
                                </td>
                                <td className={`w-[100px] border-y px-3 py-4 align-top ${brandClasses.subtleBorder}`}>
                                  <div className="flex flex-col gap-2">
                                    <Badge variant={link.isActive ? 'success' : 'warning'}>{link.isActive ? 'Active' : 'Inactive'}</Badge>
                                    {canPublishAndManageLinks ? (
                                      <Checkbox
                                        checked={link.isActive}
                                        onChange={(event) => void handleToggleLinkField(link, 'isActive', event.target.checked)}
                                      />
                                    ) : null}
                                  </div>
                                </td>
                                <td className={`w-[100px] border-y px-3 py-4 align-top ${brandClasses.subtleBorder}`}>
                                  <div className="flex flex-col gap-2">
                                    <span className={`text-sm ${brandClasses.bodyText}`}>{link.allowComments ? 'Enabled' : 'Disabled'}</span>
                                    {canPublishAndManageLinks ? (
                                      <Checkbox
                                        checked={link.allowComments}
                                        onChange={(event) => void handleToggleLinkField(link, 'allowComments', event.target.checked)}
                                      />
                                    ) : null}
                                  </div>
                                </td>
                                <td className={`border-y px-3 py-4 align-top ${brandClasses.subtleBorder}`}>
                                  <span className={`text-sm ${brandClasses.bodyText}`}>{link.lastAccessedAt ? formatDateTime(link.lastAccessedAt) : '-'}</span>
                                </td>
                                <td className={`w-[60px] border-y px-3 py-4 align-top ${brandClasses.subtleBorder}`}>
                                  <span className={`text-sm font-semibold ${brandClasses.primaryText}`}>{link.totalViews}</span>
                                </td>
                                <td className={`w-[156px] border-y px-3 py-4 align-top ${brandClasses.subtleBorder}`}>
                                  <div className="grid grid-cols-4 gap-1">
                                    <button type="button" className={actionIconButtonClassName} onClick={() => setSelectedLinkId(link.id)} title="View analytics">
                                      <Icon name="calendar" size="sm" />
                                    </button>
                                    <button type="button" className={actionIconButtonClassName} onClick={() => handleOpenEditLinkModal(link)} title="Edit link">
                                      <Icon name="edit" size="sm" />
                                    </button>
                                    <button type="button" className={actionIconButtonClassName} onClick={() => handleOpenDuplicateLinkModal(link)} title="Duplicate link">
                                      <Icon name="copy" size="sm" />
                                    </button>
                                    <button type="button" className={actionIconButtonClassName} onClick={() => void handleCopyPublicUrl(link)} title="Copy public URL">
                                      <Icon name="share" size="sm" />
                                    </button>
                                    <button type="button" className={actionIconButtonClassName} onClick={() => void handleOpenPublicPage(link)} title="Open public page">
                                      <Icon name="arrowRight" size="sm" />
                                    </button>
                                    {canPublishAndManageLinks ? (
                                      <>
                                        <button type="button" className={actionIconButtonClassName} onClick={() => void handleRegenerateToken(link)} title="Regenerate token">
                                          <Icon name="upload" size="sm" />
                                        </button>
                                        <button type="button" className={actionIconButtonClassName} onClick={() => void handleDeleteLink(link)} title="Delete public link">
                                          <Icon name="delete" size="sm" />
                                        </button>
                                      </>
                                    ) : null}
                                  </div>
                                </td>
                                <td className={`rounded-r-2xl border-y border-r px-3 py-4 align-top ${brandClasses.subtleBorder}`}>
                                  <div className="flex flex-wrap gap-2">
                                    <button type="button" className={actionIconButtonClassName} onClick={() => void handleOpenIframeModal(link)} title="View iframe code" disabled={!link.allowEmbedding}>
                                      <span className="text-xs font-bold">{'</>'}</span>
                                    </button>
                                    <button type="button" className={actionIconButtonClassName} onClick={() => void handleCopyIframe(link.id)} title="Copy iframe code" disabled={!link.allowEmbedding}>
                                      <Icon name="copy" size="sm" />
                                    </button>
                                    {canPublishAndManageLinks ? (
                                      <Checkbox
                                        checked={link.allowEmbedding}
                                        onChange={(event) => void handleToggleLinkField(link, 'allowEmbedding', event.target.checked)}
                                      />
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card className={cardClassName}>
                <CardBody className="space-y-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className={`text-2xl font-semibold ${brandClasses.primaryText}`}>Analytics Overview</h2>
                      <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>
                        {selectedLink ? `Analytics for ${selectedLink.name}` : 'Select a public link to view analytics.'}
                      </p>
                    </div>
                    {canViewAnalytics ? (
                      <Select
                        options={analyticsRangeOptions}
                        value={analyticsRange}
                        onChange={(event) => setAnalyticsRange(event.target.value)}
                        className="w-[160px]"
                      />
                    ) : null}
                  </div>

                  {!canViewAnalytics ? (
                    <p className={`text-sm ${brandClasses.bodyText}`}>Analytics are available only to Owners and Managers.</p>
                  ) : !selectedLink ? (
                    <p className={`text-sm ${brandClasses.bodyText}`}>Create a public link to view analytics.</p>
                  ) : !analytics || analytics.totalViews === 0 ? (
                    <p className={`text-sm ${brandClasses.bodyText}`}>No analytics to show yet.</p>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className={`rounded-2xl border px-4 py-4 ${brandClasses.subtleBorder}`}>
                          <div className="flex items-center gap-2">
                            <Icon name="eye" size="sm" className={brandClasses.subtleText} />
                            <p className={`text-sm ${brandClasses.subtleText}`}>Total Views</p>
                          </div>
                          <p className={`mt-3 text-3xl font-bold ${brandClasses.primaryText}`}>{analytics.totalViews}</p>
                        </div>
                        <div className={`rounded-2xl border px-4 py-4 ${brandClasses.subtleBorder}`}>
                          <div className="flex items-center gap-2">
                            <Icon name="users" size="sm" className={brandClasses.subtleText} />
                            <p className={`text-sm ${brandClasses.subtleText}`}>Unique Visitors</p>
                          </div>
                          <p className={`mt-3 text-3xl font-bold ${brandClasses.primaryText}`}>{analytics.uniqueVisitors}</p>
                        </div>
                        <div className={`rounded-2xl border px-4 py-4 ${brandClasses.subtleBorder}`}>
                          <div className="flex items-center gap-2">
                            <Icon name="share" size="sm" className={brandClasses.subtleText} />
                            <p className={`text-sm ${brandClasses.subtleText}`}>Top Referrer</p>
                          </div>
                          <p className={`mt-3 text-xl font-bold ${brandClasses.primaryText}`}>{analytics.topReferrers[0]?.domain || 'N/A'}</p>
                          <p className={`mt-1 text-sm ${brandClasses.bodyText}`}>{analytics.topReferrers[0]?.views ?? 0} views</p>
                        </div>
                        <div className={`rounded-2xl border px-4 py-4 ${brandClasses.subtleBorder}`}>
                          <div className="flex items-center gap-2">
                            <Icon name="calendar" size="sm" className={brandClasses.subtleText} />
                            <p className={`text-sm ${brandClasses.subtleText}`}>Most Active Day</p>
                          </div>
                          <p className={`mt-3 text-xl font-bold ${brandClasses.primaryText}`}>{formatDateOnly(analytics.mostActiveDay || null)}</p>
                          <p className={`mt-1 text-sm ${brandClasses.bodyText}`}>{analytics.mostActiveDayViews} views</p>
                        </div>
                      </div>

                      <div className={`rounded-xl border px-4 py-4 ${brandClasses.subtleBorder} ${brandClasses.mutedSurface}`}>
                        <p className={`text-sm font-semibold ${brandClasses.primaryText}`}>Last accessed</p>
                        <p className={`mt-2 text-sm ${brandClasses.bodyText}`}>{formatDateTime(analytics.lastAccessedAt)}</p>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>

            <div ref={moderationSectionRef}>
              <Card className={cardClassName}>
                <CardBody className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h2 className={`text-2xl font-semibold ${brandClasses.primaryText}`}>Moderation Queue</h2>
                        <Badge variant="default">{comments.length}</Badge>
                      </div>
                      <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>Review and moderate incoming public comments.</p>
                    </div>
                    <button type="button" onClick={() => setInfoMessage('Full comment management page is not added yet.')} className={`text-sm font-semibold text-[#b64f42]`}>
                      View all comments
                    </button>
                  </div>

                  {comments.length === 0 ? (
                    <div className={`flex items-center gap-4 rounded-2xl border px-5 py-5 ${brandClasses.subtleBorder} bg-[#f5f8ee]`}>
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#dce8c8] text-[#557c2d]">
                        <Icon name="check" size="md" />
                      </div>
                      <div>
                        <p className={`text-lg font-semibold ${brandClasses.primaryText}`}>No pending comments</p>
                        <p className={`text-sm ${brandClasses.bodyText}`}>Great job! You&apos;re all caught up.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {comments.map((comment) => (
                        <div key={comment.id} className={`rounded-2xl border px-4 py-4 ${brandClasses.subtleBorder} ${brandClasses.mutedSurface}`}>
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className={`font-semibold ${brandClasses.primaryText}`}>{comment.authorName}</p>
                                <Badge variant="warning">{comment.targetType}</Badge>
                              </div>
                              <p className={`mt-1 text-xs ${brandClasses.subtleText}`}>{comment.authorEmail}{comment.authorPhone ? ` · ${comment.authorPhone}` : ''}</p>
                              <p className={`mt-3 text-sm leading-6 ${brandClasses.bodyText}`}>{comment.content}</p>
                              <p className={`mt-3 text-xs ${brandClasses.subtleText}`}>Created {formatDateTime(comment.createdAt)}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button type="button" variant="outline" onClick={() => void moderateComment(comment.id, 'Approved')}>
                                Approve
                              </Button>
                              <Button type="button" variant="outline" onClick={() => void moderateComment(comment.id, 'Rejected')}>
                                Reject
                              </Button>
                              <Button type="button" variant="danger" onClick={() => void deleteComment(comment.id)}>
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        title="Public Tree Help"
        size="lg"
        footer={<Button type="button" variant="outline" onClick={() => setHelpModalOpen(false)}>Close</Button>}
      >
        <div className="space-y-4">
          <p className={`text-sm leading-6 ${brandClasses.bodyText}`}>
            Publishing creates a view-only public snapshot of your current private tree.
          </p>
          <p className={`text-sm leading-6 ${brandClasses.bodyText}`}>
            Public links let you share or embed the latest published version.
          </p>
          <p className={`text-sm leading-6 ${brandClasses.bodyText}`}>
            Private or living people are shown as Private Person by default.
          </p>
          <p className={`text-sm leading-6 ${brandClasses.bodyText}`}>
            Public comments require approval before becoming visible.
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={publishModalOpen}
        onClose={() => !publishing && setPublishModalOpen(false)}
        title="Publish public version?"
        size="lg"
        footer={(
          <>
            <Button type="button" variant="outline" onClick={() => setPublishModalOpen(false)} disabled={publishing}>Cancel</Button>
            <Button type="button" variant="primary" onClick={() => void handlePublish()} isLoading={publishing}>
              {publishing ? 'Publishing...' : 'Publish'}
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <p className={`text-sm leading-6 ${brandClasses.bodyText}`}>
            This creates a new public snapshot from the current private tree. Public links will show this new version after publishing. Private changes will not appear publicly until you publish again.
          </p>
          <TextArea
            id="publishNoteModal"
            label="Publish note"
            placeholder="Updated family information and corrected dates."
            value={publishNote}
            onChange={(event) => setPublishNote(event.target.value)}
          />
        </div>
      </Modal>

      <Modal
        isOpen={linkModalOpen}
        onClose={() => !saving && setLinkModalOpen(false)}
        title={linkForm.mode === 'edit' ? 'Edit Public Link' : linkForm.mode === 'duplicate' ? 'Duplicate Public Link' : 'Create Public Link'}
        size="lg"
        footer={(
          <>
            <Button type="button" variant="outline" onClick={() => setLinkModalOpen(false)} disabled={saving}>Cancel</Button>
            <Button type="button" variant="primary" onClick={() => void handleSaveLink()} isLoading={saving}>
              {saving ? (linkForm.mode === 'edit' ? 'Saving...' : 'Creating...') : linkForm.mode === 'edit' ? 'Save Changes' : 'Create Link'}
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <TextInput
            id="publicLinkName"
            label="Link name"
            placeholder="Family Website"
            value={linkForm.name}
            onChange={(event) => setLinkForm((current) => ({ ...current, name: event.target.value }))}
            required
          />
          <TextInput
            id="publicLinkSlug"
            label="Slug"
            placeholder="smith-family"
            value={linkForm.slug}
            onChange={(event) => setLinkForm((current) => ({ ...current, slug: normalizeSlugInput(event.target.value) }))}
            disabled={linkForm.mode === 'edit'}
          />
          {linkForm.mode === 'edit' ? (
            <p className={`text-xs ${brandClasses.subtleText}`}>Slug editing is disabled after creation. Duplicate or regenerate if you need a new share path.</p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <Checkbox
              label="Active"
              checked={linkForm.isActive}
              onChange={(event) => setLinkForm((current) => ({ ...current, isActive: event.target.checked }))}
            />
            <Checkbox
              label="Allow comments"
              checked={linkForm.allowComments}
              onChange={(event) => setLinkForm((current) => ({ ...current, allowComments: event.target.checked }))}
            />
            <Checkbox
              label="Allow embedding"
              checked={linkForm.allowEmbedding}
              onChange={(event) => setLinkForm((current) => ({ ...current, allowEmbedding: event.target.checked }))}
            />
            <Checkbox
              label="Show last name"
              checked={linkForm.showLastName}
              onChange={(event) => setLinkForm((current) => ({ ...current, showLastName: event.target.checked }))}
            />
            <Checkbox
              label="Show photo"
              checked={linkForm.showPhoto}
              onChange={(event) => setLinkForm((current) => ({ ...current, showPhoto: event.target.checked }))}
            />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={iframeModalOpen}
        onClose={() => setIframeModalOpen(false)}
        title="Iframe Embed Code"
        size="lg"
        footer={(
          <>
            <Button type="button" variant="outline" onClick={() => setIframeModalOpen(false)}>Close</Button>
            <Button type="button" variant="primary" onClick={() => void handleCopyIframe(activeIframeLinkId)}>
              Copy Code
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <p className={`text-sm ${brandClasses.bodyText}`}>Public link name: {iframeLink?.name || 'Selected link'}</p>
          <p className={`break-all text-sm ${brandClasses.bodyText}`}>{publicUrlForIframeLink || 'Regenerate this link to reveal the public URL again.'}</p>
          <div className={`rounded-xl border p-4 ${brandClasses.subtleBorder} ${brandClasses.mutedSurface}`}>
            <pre className="overflow-x-auto whitespace-pre-wrap text-sm">{iframeSnippet || 'Regenerate this link to generate fresh iframe code.'}</pre>
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
};
